import {
  Avatar,
  Descriptions,
  List,
  Typography,
  Button,
  Empty,
  Tooltip,
} from "antd";
import React, { useEffect, useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import Loader from "../../../common/loader/Loader";
import { HddOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { formatDate } from "../../../../utils/formatDate";
import { LocalDateAsUTC } from "../../../../utils/LocalDateAsUTC";
import { selectCurrentBusiness } from "../../businessesSlice";
import { useAppSelector } from "../../../../app/hooks";
import ExtractLogsFormModal from "./components/ExtractLogsFormModal";
import { IDevice } from "../../../../interfaces/businessFull.interface";
import {
  isDeviceActive,
  sortDevicesByLastOnline,
} from "./businessDevices.helpers";

export default function BusinessDevices() {
  const business = useAppSelector(selectCurrentBusiness);
  const devices = business.devices && sortDevicesByLastOnline(business.devices);
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [extractLogsFormModalShown, setExtractLogsFormModalShown] =
    useState(false);
  const activeDevicesCount = devices?.filter(isDeviceActive).length || 0;

  // Compares two dot-separated version strings numerically.
  // Returns a negative number if a < b, positive if a > b, 0 if equal.
  const compareVersions = (a: string, b: string) => {
    const aParts = a.split(".").map((n) => Number(n) || 0);
    const bParts = b.split(".").map((n) => Number(n) || 0);
    const length = Math.max(aParts.length, bParts.length);
    for (let i = 0; i < length; i++) {
      const diff = (aParts[i] || 0) - (bParts[i] || 0);
      if (diff !== 0) return diff;
    }
    return 0;
  };

  const disableLogExtraction = (device: IDevice) => {
    if (!device || !device.buildName) return true;

    let version = device.buildName;
    let buildNumber = Number(device.buildNumber);
    if (device.type === "IOS" && compareVersions(version, "1.7.6") <= 0)
      return true;
    else if (version.endsWith("lite-rapyd") && buildNumber < 455) return true;
    else if (version.endsWith("lite-y") && buildNumber < 469) return true;
    else if (version.endsWith("lite-ym") && buildNumber < 469) return true;
    else if (
      version.endsWith("y") &&
      !version.endsWith("lite-rapyd") &&
      !version.endsWith("lite-y") &&
      buildNumber < 447
    )
      return true;
    else return false;
  };

  return (
    <div style={{ position: "relative", minHeight: "100px" }}>
      {!devices ||
        (devices.length === 0 && (
          <div>
            {" "}
            <Empty description={"No devices found for this business."} />
          </div>
        ))}
      {(devices?.length || 0) > 0 && (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              marginBottom: 14,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "4px",
                borderRadius: 8,
                background: "#f5f5f5",
              }}
            >
              <div
                style={{
                  minWidth: 150,
                  textAlign: "center",
                  borderRadius: 6,
                  padding: "6px 12px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                }}
              >
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Active devices
                </Typography.Text>
                <br />
                <Typography.Text
                  strong
                  style={{
                    fontSize: 18,
                    color: activeDevicesCount > 0 ? "#1aa21a" : undefined,
                  }}
                >
                  {activeDevicesCount}
                </Typography.Text>
              </div>
              <div
                style={{
                  minWidth: 150,
                  textAlign: "center",
                  borderRadius: 6,
                  padding: "6px 12px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                }}
              >
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Total devices
                </Typography.Text>
                <br />
                <Typography.Text strong style={{ fontSize: 18 }}>
                  {devices?.length || 0}
                </Typography.Text>
              </div>
            </div>
          </div>
          <List
            size="small"
            dataSource={devices}
            renderItem={(item) => (
              <List.Item
                key={item.deviceId}
                style={{ padding: "30px 0" }}
                actions={[
                  <Tooltip
                    color="rgb(91 76 252)"
                    placement="right"
                    title={"Extract Logs"}
                  >
                    <Button
                      disabled={disableLogExtraction(item)}
                      onClick={() => {
                        setSelectedDevice(item.deviceId);
                        setExtractLogsFormModalShown(true);
                      }}
                      icon={<DownloadOutlined />}
                    ></Button>
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <span>
                      <HddOutlined
                        style={{
                          fontSize: "22px",
                          color:
                            item.type === "Android"
                              ? "#b39ddb" // Light Lavender
                              : item.type === "IOS"
                                ? "#90caf9" // Soft Blue
                                : item.type === "Pax"
                                  ? "#b0bec5" // Cool Grey
                                  : "#ffcc80", // Muted Peach
                        }}
                      />
                      <br />
                      <span style={{ width: "60px", display: "inline-block" }}>
                        {item?.type}
                      </span>
                    </span>
                  }
                  title={
                    <span style={{ width: "300px", display: "inline-block" }}>
                      {item?.deviceName || "No Name"}
                      <span style={{ marginLeft: "10px", opacity: 0.6 }}>
                        {item?.model}
                      </span>
                      <br />
                      <span></span>
                    </span>
                  }
                  description={`${item?.deviceId || ""}`}
                ></List.Item.Meta>
                <List.Item.Meta
                  style={{ marginLeft: "60px" }}
                  title={
                    <span>
                      <span style={{ opacity: 0.5 }}> Version:</span>{" "}
                      <span> {item?.buildName || "-"} </span>
                      {"  "}
                      <span
                        style={{ opacity: 0.6 }}
                      >{`(${item?.buildNumber})`}</span>
                    </span>
                  }
                  description={
                    item?.type == "Pax"
                      ? `Rapyd Core Version: ${
                          item?.paxTerminalInfo?.rapydCoreVersion || "-"
                        }`
                      : ""
                  }
                ></List.Item.Meta>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    width: "200px",
                  }}
                >
                  <span>
                    {isDeviceActive(item) ? (
                      <Avatar
                        shape="circle"
                        size="small"
                        style={{
                          backgroundColor: "rgb(26, 162, 26)",
                          opacity: "0.8",
                          boxShadow: "1px 1px 5px grey",
                        }}
                      />
                    ) : (
                      <Avatar
                        size="small"
                        shape="circle"
                        icon={<ClockCircleOutlined />}
                      />
                    )}
                  </span>
                  <span
                    style={{
                      marginLeft: "10px",
                      fontSize: "smaller",
                      color: "gray",
                    }}
                  >
                    {item.lastOnline
                      ? formatDate(new LocalDateAsUTC(item?.lastOnline).rawDate)
                      : "N/A"}
                  </span>
                </div>
              </List.Item>
            )}
          />
        </>
      )}
      <ExtractLogsFormModal
        visible={extractLogsFormModalShown}
        setVisible={setExtractLogsFormModalShown}
        deviceId={selectedDevice}
      />
    </div>
  );
}
